import {
    Component,
    EventEmitter,
    HostListener, inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { Role, SignSignatory } from '@shared/models/signrequest/sign.signatory';
import { ContactsService } from '@services/contacts/contacts.service';
import { Smstan } from '@shared/models/signrequest/authentication/smstan';
import { AuthMethod, ExclusiveMethodsArray, GlobalMethodsArray, areMethodsCompatible } from '@shared/models/settings/auth-method';
import * as _ from 'lodash';
import { Settings } from '@shared/models/settings/settings';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ModalContentComponent } from '@shared/components/modal-content/modal-content.component';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { SignrequestUtils } from '@utils/signrequest-utils';
import { UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { AddContactComponent } from '@shared/components/add-contact/add-contact.component';
import { phoneNumberValidator, validatePhoneNumber } from '@utils/phone-number.validator';
import { EnabledAuthMethod } from '@shared/models/settings/enabled-auth-method';
import { Contact } from '@shared/models/contact/contact';
import { UserService } from '@services/user/user.service';
import { Idin } from '@shared/models/signrequest/authentication/idin';
import { Irma } from '@shared/models/signrequest/authentication/irma';
import { AuthenticationMethod } from '@shared/models/signrequest/authentication/authentication-method';
import { SignDocumentRequestService } from '@routes/sign-request/services/sign-document-request.service';
import { AmDocumentTemplate } from '@shared/models/signrequest/authentication/document-template';
import { AuthTypeEnum, BaseAuthenticationMethod } from '@shared/models/signrequest/authentication/base-authentication-method';
import { Subscription } from 'rxjs';
import { getAuthType } from '@shared/models/signrequest/authentication/base-authentication-method';
import { Sso } from '@shared/models/signrequest/authentication/sso';

import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AuthMethodsComponent } from '../../auth-methods/auth-methods.component';
import { TemplateSelectorComponent } from '../../template-selector/template-selector.component';
import { EmailInputComponent } from '@shared/components/email-input/email-input.component';
import { LanguageInputComponent } from '@shared/components/language-input/language-input.component';
import {Csc} from '@shared/models/signrequest/authentication/csc';
import {CscProvider, CscUtils} from 'src/app/shared/csc/csc-util'

@Component({
    selector: 'app-sr-recipient-details',
    templateUrl: './sr-recipient-details.component.html',
    styleUrls: ['./sr-recipient-details.component.scss'],
    providers: [BsModalService],
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule, AuthMethodsComponent, TemplateSelectorComponent, EmailInputComponent, LanguageInputComponent]
})
export class SrRecipientDetailsComponent implements OnInit, OnChanges, OnDestroy {
    @Input() recipient!: SignSignatory;
    @Input() title!: string;
    @Input() description!: string;
    @Input() availableMethods!: EnabledAuthMethod[];
    @Input() addedRecipients: SignSignatory[] = [];
    @Input() settings!: Settings;
    @Input() readonly = false;
    @Input() isEdelivery = false;
    @Output() updateRecipient: EventEmitter<unknown> = new EventEmitter<unknown>();
    @Output() invalidRecipient: EventEmitter<boolean> = new EventEmitter<boolean>();
    defaultEnabledAuthIDIN!: boolean;
    defaultEnabledAuthIRMA!: boolean;
    defaultEnabledAuthCSC!: boolean;
    noContactFound = false;
    contactFilter!: string;
    contactsMap!: Map<string, Contact[]>;
    contactsMapKeys!: string[];
    contacts!: Contact[];
    allContacts: Contact[] = [];
    enabledAuthenticationMethods!: EnabledAuthMethod[];
    disabledAuthenticationMethods: AuthMethod[] = [];
    enabledPreviewMethods!: EnabledAuthMethod[];
    exclusiveMethod?: AuthMethod;
    showContactList = false;
    smstanAuthMethod?: Smstan;
    cscAuthMethod?: Csc;
    smstanAuthPreviewMethod?: Smstan;
    cscAuthPreviewMethod?: Csc
    idinAuthPreviewMethod?: Idin;
    irmaAuthPreviewMethod?: Irma;
    showProviders = false;
    ssoAuthMethod?: Sso;
    ssoAuthPreviewMethod?: Sso;
    errorMsg: string | null = null;
    selectedTemplate: string | null = null;
    readonly form: UntypedFormGroup;
    private bsModalRef!: BsModalRef;
    private tempAuthmethods: AuthenticationMethod[] = [];
    private subscription: Subscription = new Subscription();

    public enabledProviders: CscProvider[] = [];
    private cscUtils = new CscUtils();

    protected readonly modalService: BsModalService = inject(BsModalService);
    protected readonly contactsService: ContactsService = inject(ContactsService);
    protected readonly translateService: TranslateService = inject(TranslateService);
    protected readonly fb: UntypedFormBuilder = inject(UntypedFormBuilder);
    protected readonly userService: UserService = inject(UserService);
    protected readonly signDocumentRequestService: SignDocumentRequestService = inject(SignDocumentRequestService);

    constructor(
    ) {
        this.enabledProviders = _.cloneDeep(this.cscUtils.enabledProviders);

        this.form = this.fb.group({
            automaticValidationIDIN: [false],
            automaticValidationIRMA: [false],
            automaticValidationCSC: [false],
            phonenumber: ['', [Validators.required, phoneNumberValidator(true)]],
            initials: ['', [Validators.required]],
            prefix: ['', []],
            lastname: ['', [Validators.required]],
            firstname: ['', [Validators.required]],
            dateofbirth: ['', [Validators.required]]
        });
    }

    get phoneNumber() {
        return this.form.get('phonenumber');
    }

    get initials() {
        return this.form.get('initials');
    }

    get prefix() {
        return this.form.get('prefix');
    }

    get lastName() {
        return this.form.get('lastname');
    }

    get firstName() {
        return this.form.get('firstname');
    }

    get dateOfBirth() {
        return this.form.get('dateofbirth');
    }

    get automaticValidationIDIN() {
        return this.form.get('automaticValidationIDIN');
    }

    get automaticValidationIRMA() {
        return this.form.get('automaticValidationIRMA');
    }

    get automaticValidationCSC() {
        return this.form.get('automaticValidationCSC');
    }

    @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: KeyboardEvent) {
        this.toggleContactList(false, event);
    }

    ngOnInit() {
        this.getContacts();

        this.initializeAutomaticValidation();

        if (this.isEdelivery) {
            this.enabledPreviewMethods = this.availableMethods;
        } else {
            this.enabledAuthenticationMethods = this.availableMethods.filter(am => !am.authenticationMethod.endsWith('_preview'));
            this.enabledPreviewMethods = this.availableMethods.filter(am => am.authenticationMethod.endsWith('_preview'));
        }

        this.documentTemplateCheck();
        this.onRecipientUpdate();
        this.exclusiveCheck();

        this.phoneNumber?.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
        ).subscribe((res) => {
            this.recipient.phone = res;
            if (this.smstanAuthMethod) {
                this.smstanAuthMethod.mobilePhone = res;
            }
            if (this.smstanAuthPreviewMethod) {
                this.smstanAuthPreviewMethod.mobilePhone = res;
            }
            this.patchAuthMethodFields();
            this.validateRecipient();
        });

        this.initials?.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
        ).subscribe((res) => {
            if (this.idinAuthPreviewMethod && this.automaticValidationIDIN?.value) {
                this.idinAuthPreviewMethod.initials = res;
            }
            if (this.irmaAuthPreviewMethod && this.automaticValidationIRMA?.value) {
                this.irmaAuthPreviewMethod.initials = res;
            }
            this.patchAuthMethodFields();
            this.validateRecipient();
        });

        this.prefix?.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
        ).subscribe((res) => {
            if (this.idinAuthPreviewMethod) {
                this.idinAuthPreviewMethod.legalLastnamePrefix = res;
            }
            this.patchAuthMethodFields();
            this.validateRecipient();
        });

        this.lastName?.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
        ).subscribe((res) => {
            if (this.idinAuthPreviewMethod && this.automaticValidationIDIN?.value) {
                this.idinAuthPreviewMethod.legalLastname = res;
            }
            if (this.irmaAuthPreviewMethod && this.automaticValidationIRMA?.value) {
                this.irmaAuthPreviewMethod.surname = res;
            }
            if (this.cscAuthPreviewMethod && this.automaticValidationCSC?.value) {
                this.cscAuthPreviewMethod.surName = res;
            }
            this.patchAuthMethodFields();
            this.validateRecipient();
        });

        this.dateOfBirth?.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
        ).subscribe((res) => {
            if (this.idinAuthPreviewMethod && this.automaticValidationIDIN?.value) {
                this.idinAuthPreviewMethod.dateofbirth = res;
            }
            if (this.irmaAuthPreviewMethod && this.automaticValidationIRMA?.value) {
                this.irmaAuthPreviewMethod.dateofbirth = res;
            }
            if (this.cscAuthPreviewMethod && this.automaticValidationCSC?.value) {
                this.cscAuthPreviewMethod.birthDate = res;
            }
            this.patchAuthMethodFields();
            this.validateRecipient();
        });

        this.firstName?.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
        ).subscribe((res) => {
            if (this.cscAuthPreviewMethod && this.automaticValidationCSC?.value) {
                this.cscAuthPreviewMethod.givenName = res;
            }
            this.patchAuthMethodFields();
            this.validateRecipient();
        });

        this.modalService.onHidden
            .pipe(
                filter(() => this.bsModalRef !== undefined && this.bsModalRef.content instanceof AddContactComponent),
                map(() => {
                    const newContact: Contact = this.bsModalRef.content.contact;
                    return (newContact.email && newContact.name) ? newContact : null;
                })
            ).subscribe((contact) => {
                if (contact && this.emailAdded(contact.email)) {
                    const initialState = {
                        message: this.translateService.instant('signrequest.duplicate_email', { 'email': contact.email }),
                        title: this.translateService.instant('signrequest.duplicate_email_title')
                    };
                    this.bsModalRef = this.modalService.show(ModalContentComponent, { initialState });
                } else if (contact) {
                    this.allContacts.push(contact);
                    if (this.recipient != null) {
                        this.selectAvailableContact(contact, this.recipient, null);
                    }
                    this.contacts = this.allContacts;
                    this.initContactsMap();
                }
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['recipient'] && !changes['recipient'].firstChange && this.recipient) {
            this.contactFilter = '';
            this.documentTemplateCheck();
            this.onRecipientUpdate();
        }
        this.fixGlobals();
        this.exclusiveCheck();
        this.updateDisabledMethods();
    }

    ngOnDestroy() {
        if (this.contactFilter && !this.recipient.name) {
            this.recipient.name = this.contactFilter;
        }
        this.subscription.unsubscribe();
    }

    onRecipientUpdate() {
        this.onAuthMethodUpdate();
        this.phoneNumber?.patchValue(
            this.smstanAuthMethod?.mobilePhone
            || this.smstanAuthPreviewMethod?.mobilePhone
            || this.recipient?.phone
            || ''
        );
        this.initials?.patchValue(
            this.idinAuthPreviewMethod?.initials
            || this.irmaAuthPreviewMethod?.initials
            || undefined
        );
        this.prefix?.patchValue(
            this.idinAuthPreviewMethod?.legalLastnamePrefix
            || undefined
        );
        this.lastName?.patchValue(
            this.idinAuthPreviewMethod?.legalLastname
            || this.irmaAuthPreviewMethod?.surname
            || this.cscAuthPreviewMethod?.surName
            || this.nameToLastName(this.recipient?.name)
            || undefined
        );
        this.dateOfBirth?.patchValue(
            this.idinAuthPreviewMethod?.dateofbirth
            || this.irmaAuthPreviewMethod?.dateofbirth
            || this.cscAuthPreviewMethod?.birthDate
            || undefined
        );
        this.firstName?.patchValue(
            this.cscAuthPreviewMethod?.surName
            || this.nameToFirstNames(this.recipient?.name)
            || undefined
        );
        this.patchAuthMethodFields();
        this.validateRecipient();

        this.onAuthMethodUpdate();
        this.syncProvidersFromData();
    }

    onRecipientSelect() {
        if (this.recipient.signatoryRole !== Role.SIGN) {
            this.recipient.authenticationMethods = [];
        }
        this.onAuthMethodUpdate();
        this.initials?.patchValue('');
        this.prefix?.patchValue('');
        this.lastName?.patchValue(this.nameToLastName(this.recipient?.name));
        this.firstName?.patchValue(this.nameToFirstNames(this.recipient?.name));
        this.dateOfBirth?.patchValue('');
        this.phoneNumber?.patchValue(this.recipient?.phone || '');
        this.patchAuthMethodFields();
        this.validateRecipient();
    }

    onAuthMethodUpdate() {
        this.smstanAuthPreviewMethod =
            this.recipient.authenticationMethods.find(am => getAuthType(am) === AuthTypeEnum.PREVIEW && am.type === 'smstan') as Smstan;
        this.irmaAuthPreviewMethod =
            this.recipient.authenticationMethods.find(am => getAuthType(am) === AuthTypeEnum.PREVIEW && am.type === 'irma') as Irma;
        this.idinAuthPreviewMethod =
            this.recipient.authenticationMethods.find(am => getAuthType(am) === AuthTypeEnum.PREVIEW && am.type === 'idin') as Idin;
        this.ssoAuthPreviewMethod =
            this.recipient.authenticationMethods.find(am => getAuthType(am) === AuthTypeEnum.PREVIEW && am.type === 'sso') as Sso;
        this.cscAuthPreviewMethod =
            this.recipient.authenticationMethods.find(am => getAuthType(am) === AuthTypeEnum.PREVIEW && am.type === 'csc') as Csc;

        this.smstanAuthMethod =
            this.recipient.authenticationMethods.find(am => getAuthType(am) === AuthTypeEnum.AFTERVIEW && am.type === 'smstan') as Smstan;
        this.cscAuthMethod =
            this.recipient.authenticationMethods.find(am => getAuthType(am) === AuthTypeEnum.AFTERVIEW && am.type === 'csc') as Csc;
        this.ssoAuthMethod =
            this.recipient.authenticationMethods.find(am => getAuthType(am) === AuthTypeEnum.AFTERVIEW && am.type === 'sso') as Sso;
    }

    patchAuthMethodFields() {
        if (this.smstanAuthMethod) {
            this.smstanAuthMethod.mobilePhone = this.phoneNumber?.value;
        }
        if (this.smstanAuthPreviewMethod) {
            this.smstanAuthPreviewMethod.mobilePhone = this.phoneNumber?.value;
        }
        if (this.irmaAuthPreviewMethod) {
            if ( this.isEdelivery ) {
                this.automaticValidationIRMA?.patchValue((this.recipient.authenticationMethods
                    .find(am => am.type === 'irma') as Irma).automaticValidation);
            }
            this.irmaAuthPreviewMethod.initials = this.automaticValidationIRMA?.value ? this.initials?.value : undefined;
            this.irmaAuthPreviewMethod.surname = this.automaticValidationIRMA?.value ? this.lastName?.value : undefined;
            this.irmaAuthPreviewMethod.dateofbirth = this.automaticValidationIRMA?.value ? this.dateOfBirth?.value : undefined;
        }
        if (this.idinAuthPreviewMethod) {
            if ( this.isEdelivery ) {
                this.automaticValidationIDIN?.patchValue((this.recipient.authenticationMethods
                    .find(am => am.type === 'idin') as Idin).automaticValidation);
            }
            this.idinAuthPreviewMethod.initials = this.automaticValidationIDIN?.value ? this.initials?.value : undefined;
            this.idinAuthPreviewMethod.legalLastname = this.automaticValidationIDIN?.value ? this.lastName?.value : undefined;
            this.idinAuthPreviewMethod.legalLastnamePrefix = this.automaticValidationIDIN?.value ? this.prefix?.value : undefined;
            this.idinAuthPreviewMethod.dateofbirth = this.automaticValidationIDIN?.value ? this.dateOfBirth?.value : undefined;
        }
        if (this.cscAuthPreviewMethod) {
            if ( this.isEdelivery ) {
                this.automaticValidationCSC?.patchValue((this.recipient.authenticationMethods
                    .find(am => am.type === 'csc') as Csc).automaticValidation);
            }
            this.cscAuthPreviewMethod.surName = this.automaticValidationCSC?.value ? this.lastName?.value : undefined;
            this.cscAuthPreviewMethod.givenName = this.automaticValidationCSC?.value ? this.firstName?.value : undefined;
            this.cscAuthPreviewMethod.birthDate = this.automaticValidationCSC?.value ? this.dateOfBirth?.value : undefined;
        }
    }

    documentTemplateCheck() {
        if (!this.isEdelivery) {
            this.selectedTemplate = this.signDocumentRequestService.template;
        }
    }

    nameToLastName(name?: string): string {
        if (!name) {
            return '';
        }
        return name.substring(name.lastIndexOf(' ') + 1);
    }

    nameToFirstNames(name?: string): string {
        if (!name) {
            return '';
        }
        const trimmed = name.trim();
        const lastSpace = trimmed.lastIndexOf(' ');
        return lastSpace === -1 ? trimmed : trimmed.substring(0, lastSpace);
    }


    automaticValidationTranslationKey(translationKey: string, field: string) {
        const irmaMethods = ['initials','lastName','dateOfBirth']
        const idinMethods = ['initials','prefix','lastName','dateOfBirth']
        const cscMethods = ['firstName','lastName','dateOfBirth']
        switch (+(!!this.irmaAuthPreviewMethod && !!this.automaticValidationIRMA?.value && irmaMethods.includes(field)) +
        +(!!this.idinAuthPreviewMethod && !!this.automaticValidationIDIN?.value && idinMethods.includes(field)) +
        +(!!this.cscAuthPreviewMethod && !!this.automaticValidationCSC?.value && cscMethods.includes(field))) {
            case 2:
                return translationKey + "_2";
            case 3:
                return translationKey + "_3";
            default:
                return translationKey + "_1"
        }
    }

    automaticValidationTranslationMethods(field: string) {
        const irmaMethods = ['initials','lastName','dateOfBirth']
        const idinMethods = ['initials','prefix','lastName','dateOfBirth']
        const cscMethods = ['firstName','lastName','dateOfBirth']
        const result: Record<string, string> = {};
        if (!!this.irmaAuthPreviewMethod && !!this.automaticValidationIRMA?.value && irmaMethods.includes(field)) {
            result[`method${Object.keys(result).length + 1}`] = 'Yivi'
        }
        if (!!this.idinAuthPreviewMethod && !!this.automaticValidationIDIN?.value && idinMethods.includes(field)) {
            result[`method${Object.keys(result).length + 1}`] = 'iDIN'
        }
        if (!!this.cscAuthPreviewMethod && !!this.automaticValidationCSC?.value && cscMethods.includes(field)) {
            result[`method${Object.keys(result).length + 1}`] = 'CSC'
        }
        return result;
    }

    filterContacts() {
        if (!this.contactFilter) {
            this.contacts = this.allContacts;
        } else {
            this.contacts = this.allContacts?.filter(contact => contact.name.toLowerCase().includes(this.contactFilter.toLowerCase()));
        }

        this.initContactsMap();
    }

    createContact(signatory: SignSignatory) {
        this.bsModalRef = this.modalService.show(
            AddContactComponent,
            Object.assign({}, { keyboard: false, ignoreBackdropClick: true })
        );

        const contact = new Contact();
        if (signatory.name || this.contactFilter) {
            contact.name = signatory.name || this.contactFilter;
        }
        if (signatory.email) {
            contact.email = signatory.email;
        }
        if (signatory.phone) {
            contact.phone = signatory.phone;
        }

        contact.locale = signatory.locale ?? 'nl';

        this.bsModalRef.content.setContact(contact, true);
        this.bsModalRef.content.setSubmitMode(AddContactComponent.SUBMIT_LOCAL);
    }

    /**
     * Return bool if email address matches with addedRecipients
     */
    emailAdded(email: string): boolean {
        return this.addedRecipients.some(recipient => email === recipient.email);
    }


    selectAvailableContact(contact: Contact, signatory: SignSignatory, event: Event | null) {
        signatory.name = contact.name;
        signatory.email = contact.email;
        signatory.phone = contact.phone ?? '';
        signatory.privacyProtected = contact.privacyProtected ?? false;
        signatory.locale = contact.locale ?? 'nl';

        const authMethods = contact.authenticationMethods
            .filter(am => !this.disabledAuthenticationMethods.includes(am as AuthMethod));
        const globalMethod = this.findGlobalMethod();
        if (globalMethod) {
            authMethods.push(globalMethod);
        }

        if (authMethods.length) {
            this.exclusiveMethod = undefined;
            signatory.authenticationMethods = [];
        }

        this.enabledPreviewMethods
            .filter(am => am.defaultEnabled)
            .map(am => this.toggleAuthentication(am.authenticationMethod, true));

        this.initializeContactAuthMethods(authMethods);

        this.onRecipientSelect();
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        this.toggleContactList(false, null);
    }

    /**
     * Set recipients role for the signing process
     * @param signRole
     */
    setRole(signRole: string): void {
        this.recipient.signatoryRole = Role[signRole as Role];

        if (signRole === 'SIGN') {
            this.recipient.authenticationMethods = this.tempAuthmethods;
        } else {
            this.tempAuthmethods = this.recipient.authenticationMethods;
            this.recipient.authenticationMethods = [];
        }
        this.signDocumentRequestService.validate();
        this.errorMsg = this.recipientErrorMsg();

    }

    updateEnabledProviders(providers: CscProvider[], methodsArray: AuthenticationMethod[]) {
        if (!methodsArray) return;

        const cscMethod: Csc = methodsArray.find(am => am.type === 'csc' || am.name === 'csc') as Csc;

        if (cscMethod) {
            const enabledProviders = providers
                .filter(p => p.selected)
                .map(p => p.id);

            cscMethod.allowedCSCProviders = enabledProviders;
            console.log("Enabled Providers List for this recipient:", cscMethod.allowedCSCProviders);
        }
    }

    toggleAuthentication(method: AuthMethod, preview = false) {
        if (this.isEdelivery && !preview && !method.endsWith('_preview')) {
            return;
        }

        const isGlobal = GlobalMethodsArray.includes(method);
        const index = this.recipient.authenticationMethods.findIndex(am =>
            SignrequestUtils.findMethodByType(am, method, preview)
        );
        const turningOn = index === -1;

        if (isGlobal) {
            this.addedRecipients.forEach(recp => {
                if (!recp.authenticationMethods) {
                    recp.authenticationMethods = [];
                }

                const rIdx = recp.authenticationMethods.findIndex(am =>
                    SignrequestUtils.findMethodByType(am, method, preview)
                );

                if (turningOn && rIdx === -1) {
                    const newAuth = SignrequestUtils.setAuthMethods(method);
                    if (newAuth) {
                        recp.authenticationMethods = recp.authenticationMethods.filter(am => {
                            const type = SignrequestUtils.normalizeAuthType(am.name || am.type || (am as unknown as Record<string, string>)['authenticationMethod']);
                            return !ExclusiveMethodsArray.includes(type) ||
                                areMethodsCompatible(type, method);
                        });
                        recp.authenticationMethods.push(newAuth);
                    }
                } else if (!turningOn && rIdx !== -1) {
                    recp.authenticationMethods.splice(rIdx, 1);
                }
            });
        } else {
            const authmethods = _.cloneDeep(this.recipient.authenticationMethods);
            if (turningOn) {
                const newAuthMethod = SignrequestUtils.setAuthMethods(method);
                if (newAuthMethod) {
                    const filtered = authmethods.filter(am => {
                        const type = SignrequestUtils.normalizeAuthType(am.name || am.type || (am as unknown as Record<string, string>)['authenticationMethod']);
                        return !ExclusiveMethodsArray.includes(type as AuthMethod) ||
                            areMethodsCompatible(type as AuthMethod, method);
                    });
                    filtered.push(newAuthMethod);
                    this.recipient.authenticationMethods = filtered;
                    if (method === 'csc') {
                        this.showProviders = true;
                        (newAuthMethod as Csc).allowedCSCProviders = this.enabledProviders
                            .filter(p => p.selected)
                            .map(p => p.id);
                    }
                }
            } else {
                authmethods.splice(index, 1);
                if (method === 'csc') {
                    this.showProviders = false;
                    this.enabledProviders.forEach(p => p.selected = false);
                }
                this.recipient.authenticationMethods = authmethods;
            }
        }

        // These two lines were accidentally removed
        this.onRecipientUpdate();
        this.updateDisabledMethods();
    }

    isSmsAuthMethod(am: AuthenticationMethod): am is Smstan {
        return am.type === 'smstan';
    }

    isIdinAuthMethod(am: AuthenticationMethod): am is Idin {
        return am.type === 'idin';
    }

    isIrmaAuthMethod(am: AuthenticationMethod): am is Irma {
        return am.type === 'irma';
    }

    isCscAuthMethod(am: AuthenticationMethod): am is Csc {
        return am.type === 'csc';
    }

    isDocumentTemplateAuthMethod(am: AuthenticationMethod): am is AmDocumentTemplate {
        return am.type === 'documenttemplate';
    }

    toggleExclusiveMethod(method: BaseAuthenticationMethod, checked: boolean, global: boolean) {
        if (this.recipient.signatoryRole !== 'SIGN') {
            return;
        }

        const index = this.recipient.authenticationMethods
            .findIndex(am => SignrequestUtils.findMethodByType(am, method.type as AuthMethod));

        if (!checked && (-1 !== index) && global) {
            this.recipient.authenticationMethods.splice(index, 1);
            this.exclusiveMethod = undefined;
        }

        if (checked && (-1 === index && global)) {
            this.recipient.authenticationMethods.push(method);
            this.exclusiveMethod = method.type as AuthMethod;
        }
    }

    validateRecipient() {
        let recipientInvalid: boolean;
        if (!this.recipient.name && !this.recipient.email) {
            // Empty recipient, mark as invalid without error message
            this.errorMsg = null;
            recipientInvalid = true;
        } else {
            this.errorMsg = this.recipientErrorMsg();
            recipientInvalid = !!this.errorMsg;
        }
        this.invalidRecipient.emit(recipientInvalid);
        this.updateRecipient.emit();
    }

    recipientErrorMsg(): string | null {
        if (!!this.signDocumentRequestService.getSignatoriesByRole([Role.ACCEPTANCE]).length
            && !this.signDocumentRequestService.getSignatoriesByRole([Role.SIGN, Role.DELIVERY]).length) {
            return this.translateService.instant('signrequest.signatory_atleast_one_sign');
        }

        if (!this.recipient.name) {
            return this.translateService.instant('signrequest.signatory_name_error');
        }

        if (!this.recipient.email) {
            return this.translateService.instant('signrequest.signatory_email_error');
        } else if (
            this.addedRecipients.filter(signatory => signatory.email?.toLowerCase() === this.recipient.email?.toLowerCase()).length > 1
        ) {
            return this.translateService.instant('signrequest.duplicate_email', { 'email': this.recipient.email });
        }

        const requiredAmRoles = [Role.SIGN, Role.DELIVERY];

        if (requiredAmRoles.includes(this.recipient.signatoryRole)) {
            if (!this.isEdelivery
                && !this.recipient.authenticationMethods.filter(am => getAuthType(am) === 'AFTERVIEW').length) {
                return this.translateService.instant('signrequest.authmethod_error.emtpy');
            }
            if (this.isEdelivery && !this.recipient.authenticationMethods.filter(am => getAuthType(am) === 'PREVIEW').length) {
                return this.translateService.instant('edelivery.authmethod_error.emtpy');
            }
            for (const am of this.recipient.authenticationMethods) {
                if (this.isSmsAuthMethod(am)) {
                    if (!this.phoneNumber?.value?.length) {
                        return this.translateService.instant('signrequest.authmethod_error.nophone');
                    }
                    if (!this.phoneNumber?.value?.length || !validatePhoneNumber(true)(this.phoneNumber?.value)) {
                        return this.translateService.instant('signrequest.authmethod_error.wrongphone');
                    }
                } else if (this.isIdinAuthMethod(am) && getAuthType(am) === 'PREVIEW') {
                    if (this.automaticValidationIDIN?.value) {
                        if (!this.initials?.value?.length) {
                            return this.translateService.instant( this.automaticValidationTranslationKey('signrequest.authmethod_error.noinitials','initials'),this.automaticValidationTranslationMethods('initials'));
                        }
                        if (!this.lastName?.value?.length) {
                            return this.translateService.instant( this.automaticValidationTranslationKey('signrequest.authmethod_error.nolastname','lastName'),this.automaticValidationTranslationMethods('lastName'));
                        }
                        if (!this.dateOfBirth?.value?.length) {
                            return this.translateService.instant( this.automaticValidationTranslationKey('signrequest.authmethod_error.nodateofbirth','dateOfBirth'),this.automaticValidationTranslationMethods('dateOfBirth'));
                        }
                    }
                } else if (this.isIrmaAuthMethod(am) && getAuthType(am) === 'PREVIEW') {
                    if (this.automaticValidationIRMA?.value) {
                        if (!this.initials?.value?.length) {
                            return this.translateService.instant( this.automaticValidationTranslationKey('signrequest.authmethod_error.noinitials','initials'),this.automaticValidationTranslationMethods('initials'));
                        }
                        if (!this.lastName?.value?.length) {
                            return this.translateService.instant( this.automaticValidationTranslationKey('signrequest.authmethod_error.nolastname','lastName'),this.automaticValidationTranslationMethods('lastName'));
                        }
                        if (!this.dateOfBirth?.value?.length) {
                            return this.translateService.instant( this.automaticValidationTranslationKey('signrequest.authmethod_error.nodateofbirth','dateOfBirth'),this.automaticValidationTranslationMethods('dateOfBirth'));
                        }
                    }
                } else if (this.isCscAuthMethod(am) && getAuthType(am) === 'PREVIEW') {
                    if (this.automaticValidationCSC?.value) {
                        if (!this.firstName?.value?.length) {
                            return this.translateService.instant( this.automaticValidationTranslationKey('signrequest.authmethod_error.nofirstname','firstName'),this.automaticValidationTranslationMethods('firstName'));
                        }
                        if (!this.lastName?.value?.length) {
                            return this.translateService.instant( this.automaticValidationTranslationKey('signrequest.authmethod_error.nolastname','lastName'),this.automaticValidationTranslationMethods('lastName'));
                        }
                        if (!this.dateOfBirth?.value?.length) {
                            return this.translateService.instant( this.automaticValidationTranslationKey('signrequest.authmethod_error.nodateofbirth','dateOfBirth'),this.automaticValidationTranslationMethods('dateOfBirth'));
                        }
                    }
                } else if (this.isDocumentTemplateAuthMethod(am)) {
                    if (!this.signDocumentRequestService.template) {
                        return this.translateService.instant('signrequest.authmethod_error.notemplate');
                    }
                }
            }
        }
        return null;
    }

    emailErrors(errors: ValidationErrors | null) {
        if (errors) {
            this.errorMsg = 'form.invalidEmail';
            this.invalidRecipient.emit(true);
            this.updateRecipient.emit();
        }
    }

    toggleContactList(toggle: boolean, event: Event | null) {
        this.showContactList = toggle;
        if ( event ) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (!toggle) {
            if (this.contactFilter && !this.recipient.name) {
                this.recipient.name = this.contactFilter;
            }

            this.contacts = this.allContacts;
            this.initContactsMap();
        }
    }

    selectTemplate(event: string | null) {
        this.selectedTemplate = event;
        this.signDocumentRequestService.template = event;
        this.validateRecipient();
    }

    templatesAvailable(available: boolean) {
        if (!available) {
            this.disabledAuthenticationMethods.push('documenttemplate');
        }
    }

    updateDisabledMethods() {
        this.disabledAuthenticationMethods = [];

        if (!this.recipient?.authenticationMethods) {
            return;
        }

        console.log('--- updateDisabledMethods ---');
        console.log('recipient authMethods:', this.recipient.authenticationMethods.map(am => ({
            type: am.type,
            authtype: am.authtype,
            normalized: SignrequestUtils.normalizeAuthType(am.type)
        })));

        let activeGlobalMethod: AuthMethod | null = null;
        for (const recp of this.addedRecipients) {
            const found = recp.authenticationMethods?.find(am => {
                const type = SignrequestUtils.normalizeAuthType(am.name || am.type || (am as unknown as Record<string, string>)['authenticationMethod']);
                return GlobalMethodsArray.includes(type);
            });
            if (found) {
                activeGlobalMethod = SignrequestUtils.normalizeAuthType(found.name || found.type || (found as unknown as Record<string, string>)['authenticationMethod']);
                break;
            }
        }

        console.log('activeGlobalMethod:', activeGlobalMethod);

        for (const am of this.availableMethods) {
            const methodType = am.authenticationMethod;

            if (!ExclusiveMethodsArray.includes(methodType)) {
                continue;
            }

            console.log(`checking methodType: ${methodType}, activeGlobalMethod: ${activeGlobalMethod}, compatible: ${activeGlobalMethod ? areMethodsCompatible(activeGlobalMethod, methodType) : 'n/a'}, isGlobal: ${GlobalMethodsArray.includes(methodType)}`);

            if (activeGlobalMethod && !areMethodsCompatible(activeGlobalMethod, methodType) && GlobalMethodsArray.includes(methodType)) {
                this.disabledAuthenticationMethods.push(methodType);
                continue;
            }

            const hasLocalConflict = this.recipient.authenticationMethods.some(method => {
                const currentType = SignrequestUtils.normalizeAuthType(method.name || method.type || (method as unknown as Record<string, string>)['authenticationMethod']);
                console.log(`  local check: currentType=${currentType}, methodType=${methodType}, authtype=${method.authtype}, inExclusive=${ExclusiveMethodsArray.includes(currentType)}, compatible=${areMethodsCompatible(currentType, methodType)}`);
                return method.authtype === 'AFTERVIEW' &&
                    ExclusiveMethodsArray.includes(currentType) &&
                    !areMethodsCompatible(currentType, methodType);
            });
            if (hasLocalConflict) {
                this.disabledAuthenticationMethods.push(methodType);
            }
        }
        console.log('Disabled Methods:', this.disabledAuthenticationMethods);
    }

    /**
     * Initiate new Map from contacts sorted by name
     * @private
     */
    private initContactsMap() {
        const contactsMap = new Map<string, Contact[]>();
        if (this.contacts) {
            const sortedContacts: Contact[] = _.orderBy(this.contacts, [contact => contact.name.toLowerCase()]);

            for (const contact of sortedContacts) {
                const firstLetter = (contact.name.substring(0, 1)).toUpperCase();
                if (contactsMap.has(firstLetter)) {
                    const lContacts = contactsMap.get(firstLetter);
                    if (lContacts && lContacts.length) {
                        lContacts.push(contact);
                        contactsMap.set(firstLetter, lContacts);
                    }
                } else {
                    const lContacts: Contact[] = [contact];
                    contactsMap.set(firstLetter, lContacts);
                }
            }
        }

        this.contactsMap = contactsMap;
        this.contactsMapKeys = Array.from(contactsMap.keys());
        this.noContactFound = !this.contactsMapKeys.length;
    }

    private getContacts() {
        const department = this.userService.department;
        let service;
        if (department && this.settings.departmentFilterContacts) {
            service = this.contactsService.findByDepartment(department);
        } else {
            service = this.contactsService.findAll();
        }

        this.subscription.add(service.subscribe((contacts) => {
            this.contacts = contacts.resources;
            this.allContacts = contacts.resources;
            this.initContactsMap();
        }));
    }

    addRole($event: Event) {
        $event.preventDefault();
        $event.stopPropagation();
        const roleValue = ($event.target as HTMLInputElement).value;
        if (roleValue === '') {
            return;
        }
        let roles = this.recipient.roles;
        if (!roles) {
            roles = [];
        }
        if (roles.indexOf(roleValue) > -1) {
            return;
        }
        roles?.push(roleValue);
        this.recipient.roles = roles;
        ($event.target as HTMLInputElement).value = '';
    }

    removeRole(role: string): void {
        if (!this.recipient.roles) {
            return;
        }
        const roles = this.recipient.roles;
        if (roles) {
            roles.splice(roles.indexOf(role), 1);
        }
        if (roles.length > 0) {
            this.recipient.roles = roles;
        } else {
            this.recipient.roles = null;
        }
    }

    initializeContactAuthMethods(authMethods: string[]) {
        const exclusives: AuthMethod[] = authMethods.filter(
            (method) => ExclusiveMethodsArray.includes(method as AuthMethod)) as AuthMethod[];

        const activatedExclusives: AuthMethod[] = [];
        for (const candidate of exclusives) {
            const isCompatibleWithAll = activatedExclusives.every(active => areMethodsCompatible(active, candidate));
            if (isCompatibleWithAll) {
                this.toggleAuthentication(candidate);
                activatedExclusives.push(candidate);
            }
        }
        this.exclusiveMethod = activatedExclusives[0];

        authMethods
            .filter(method => !exclusives.includes(method as AuthMethod))
            .forEach(method => this.toggleAuthentication(method as AuthMethod));
    }

    exclusiveCheck() {
        const activeExclusives = this.recipient.authenticationMethods.filter(
            method => method.authtype === AuthTypeEnum.AFTERVIEW &&
                ExclusiveMethodsArray.includes(SignrequestUtils.normalizeAuthType(method.type))
        );
        this.exclusiveMethod = activeExclusives.length > 0
            ? SignrequestUtils.normalizeAuthType(activeExclusives[0].type)
            : undefined;
    }

    fixGlobals() {
        this.addedRecipients.forEach(recipient => {
            if (recipient.authenticationMethods == null) {
                recipient.authenticationMethods = [];
            }
        });
        const globalMethod = this.findGlobalMethod();
        if (globalMethod) {
            this.addedRecipients.forEach(recipient => {
                recipient.authenticationMethods = recipient.authenticationMethods.filter(am =>
                    !ExclusiveMethodsArray.includes(SignrequestUtils.normalizeAuthType(am.type)) ||
                    areMethodsCompatible(SignrequestUtils.normalizeAuthType(am.type), globalMethod)
                );
            });
        }

        const exclusiveAuths = this.recipient.authenticationMethods.filter(am => {
            const type = SignrequestUtils.normalizeAuthType(am.name || am.type || (am as unknown as Record<string, string>)['authenticationMethod']);
            return ExclusiveMethodsArray.includes(type);
        });

        if (exclusiveAuths.length > 1) {
            const toKeep: typeof exclusiveAuths = [];
            for (const auth of exclusiveAuths) {
                const type = SignrequestUtils.normalizeAuthType(auth.name || auth.type || (auth as unknown as Record<string, string>)['authenticationMethod']);
                const compatibleWithKept = toKeep.every(kept => {
                    const keptType = SignrequestUtils.normalizeAuthType(kept.name || kept.type || (kept as unknown as Record<string, string>)['authenticationMethod']);
                    return areMethodsCompatible(type, keptType);
                });
                if (compatibleWithKept) {
                    toKeep.push(auth);
                }
            }
            const toKeepSet = new Set(toKeep);
            this.recipient.authenticationMethods = this.recipient.authenticationMethods.filter(am => {
                const type = SignrequestUtils.normalizeAuthType(am.name || am.type || (am as unknown as Record<string, string>)['authenticationMethod']);
                return !ExclusiveMethodsArray.includes(type) || toKeepSet.has(am);
            });
        }

        const currentExclusive = this.recipient.authenticationMethods.find(am => {
            const type = SignrequestUtils.normalizeAuthType(am.name || am.type || (am as unknown as Record<string, string>)['authenticationMethod']);
            return ExclusiveMethodsArray.includes(type);
        });

        this.exclusiveMethod = currentExclusive
            ? SignrequestUtils.normalizeAuthType(currentExclusive.name || currentExclusive.type || (currentExclusive as unknown as Record<string, string>)['authenticationMethod'])
            : undefined;

        this.updateDisabledMethods();
    }

    findGlobalMethod(): AuthMethod | undefined {
        return GlobalMethodsArray.find(am =>
            !!this.addedRecipients.find(recipient =>
                recipient.authenticationMethods.find(recipam =>
                    SignrequestUtils.normalizeAuthType(recipam.type) === am)));
    }

    onAutomaticValidationCheck(authName: string) {
        if (authName === 'idin') {
            this.automaticValidationIDIN?.patchValue(!this.automaticValidationIDIN.value);
            (this.recipient.authenticationMethods.find(am => am.type === 'idin') as Idin).automaticValidation =
                this.automaticValidationIDIN?.value;
        } else if (authName === 'irma') {
            this.automaticValidationIRMA?.patchValue(!this.automaticValidationIRMA.value);
            (this.recipient.authenticationMethods.find(am => am.type === 'irma') as Irma).automaticValidation =
                this.automaticValidationIRMA?.value;
        } else if (authName === 'csc') {
            this.automaticValidationCSC?.patchValue(!this.automaticValidationCSC.value);
            (this.recipient.authenticationMethods.find(am => am.type === 'csc') as Csc).automaticValidation =
                this.automaticValidationCSC?.value;
        }
        this.onRecipientUpdate();
    }

    initializeAutomaticValidation() {
        if ( this.isEdelivery ) {
            const hasAutomaticValidationIDIN = this.settings.enabledAuthenticationMethods
                .find(am => am.authenticationMethod === 'idin_preview' && am.dmtService === 'EDELIVERY')
                ?.defaultAutomaticValidation;
            this.automaticValidationIDIN?.patchValue(hasAutomaticValidationIDIN ? hasAutomaticValidationIDIN : false);
            this.defaultEnabledAuthIDIN = hasAutomaticValidationIDIN ? hasAutomaticValidationIDIN : false;

            const hasAutomaticValidationIRMA = this.settings.enabledAuthenticationMethods
                .find(am => am.authenticationMethod === 'irma_preview' && am.dmtService === 'EDELIVERY')
                ?.defaultAutomaticValidation;
            this.automaticValidationIRMA?.patchValue(hasAutomaticValidationIRMA ? hasAutomaticValidationIRMA : false);
            this.defaultEnabledAuthIRMA = hasAutomaticValidationIRMA ? hasAutomaticValidationIRMA : false;

            const hasAutomaticValidationCSC = this.settings.enabledAuthenticationMethods
                .find(am => am.authenticationMethod === 'csc_preview' && am.dmtService === 'EDELIVERY')
                ?.defaultAutomaticValidation;
            this.automaticValidationCSC?.patchValue(hasAutomaticValidationCSC ? hasAutomaticValidationCSC : false);
            this.defaultEnabledAuthCSC = hasAutomaticValidationCSC ? hasAutomaticValidationCSC : false;
        } else {
            this.automaticValidationIDIN?.patchValue(true);
            this.automaticValidationIRMA?.patchValue(true);
            this.automaticValidationCSC?.patchValue(true);
        }
    }

    syncProvidersFromData() {
        const globalCsc = this.availableMethods.find(am => am.authenticationMethod === 'csc');

        const recipientCsc = this.recipient?.authenticationMethods
            ?.find(am => getAuthType(am) === AuthTypeEnum.AFTERVIEW && am.type === 'csc') as Csc;

        const providerSource = recipientCsc?.allowedCSCProviders?.length
            ? recipientCsc.allowedCSCProviders
            : globalCsc?.enabledProviders;

        if (providerSource && providerSource.length > 0) {
            this.enabledProviders.forEach(p => {
                p.selected = providerSource!.includes(p.id);
            });
            this.showProviders = true;
        } else {
            this.showProviders = false;
            this.enabledProviders.forEach(p => p.selected = false);
        }
    }

    public prepareForNavigation(): void {
        const selectedProviderNames = this.enabledProviders
            .filter(p => p.selected)
            .map(p => p.apiName);

        // Always find directly from recipient — don't rely on cscAuthMethod reference
        const cscMethod = this.recipient.authenticationMethods
            .find(am => getAuthType(am) === AuthTypeEnum.AFTERVIEW && am.type === 'csc') as Csc;

        if (cscMethod) {
            cscMethod.allowedCSCProviders = selectedProviderNames;
        }

        this.updateRecipient.emit(this.recipient);
        this.validateRecipient();
    }
}
